import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Star, BedDouble, Bath, Users, MapPin } from "lucide-react";
import api from "../lib/api";
import { fmtINR, toISO } from "../lib/dates";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

export default function VillasSearch() {
  const navigate = useNavigate();
  const [villas, setVillas] = useState([]);
  const [city, setCity] = useState("all");
  const [guests, setGuests] = useState("1");
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");

  useEffect(() => {
    api.get("/properties").then(({ data }) => setVillas(data.filter((v) => v.bookable))).catch(() => {});
  }, []);

  const cities = useMemo(() => ["all", ...Array.from(new Set(villas.map((v) => v.city)))], [villas]);

  const filtered = villas.filter(
    (v) => (city === "all" || v.city === city) && v.guests >= Number(guests || 1)
  );

  const openVilla = (id) => {
    const qs = new URLSearchParams();
    if (checkIn) qs.set("check_in", checkIn);
    if (checkOut) qs.set("check_out", checkOut);
    if (guests) qs.set("guests", guests);
    navigate(`/villa/${id}${qs.toString() ? `?${qs}` : ""}`);
  };

  const today = toISO(new Date());

  return (
    <div data-testid="villas-search-page" className="bg-secondary/40 min-h-screen">
      {/* Search bar */}
      <div className="bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-accent">Book a stay</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-light mt-2">Find your pine villa escape</h1>

          <div className="mt-8 bg-card text-card-foreground rounded-lg p-5 grid grid-cols-1 md:grid-cols-5 gap-4 items-end shadow-lg">
            <div className="space-y-1.5">
              <Label className="text-xs">Destination</Label>
              <Select value={city} onValueChange={setCity}>
                <SelectTrigger data-testid="search-city-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {cities.map((c) => (
                    <SelectItem key={c} value={c} className="capitalize">{c === "all" ? "All locations" : c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Check-in</Label>
              <Input type="date" min={today} data-testid="search-checkin" value={checkIn} onChange={(e) => setCheckIn(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Check-out</Label>
              <Input type="date" min={checkIn || today} data-testid="search-checkout" value={checkOut} onChange={(e) => setCheckOut(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Guests</Label>
              <Input type="number" min="1" data-testid="search-guests" value={guests} onChange={(e) => setGuests(e.target.value)} />
            </div>
            <Button data-testid="search-submit-btn" className="rounded-full h-11 gap-2" onClick={() => filtered[0] && openVilla(filtered[0].id)}>
              <Search className="h-4 w-4" /> Search
            </Button>
          </div>
        </div>
      </div>

      {/* Listings */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
        <p className="text-muted-foreground mb-6" data-testid="villas-count">{filtered.length} villas available</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
          {filtered.map((v, i) => (
            <motion.div
              key={v.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              data-testid={`villa-card-${v.id}`}
              onClick={() => openVilla(v.id)}
              className="group cursor-pointer rounded-lg overflow-hidden border border-border bg-card hover:shadow-lg transition-shadow"
            >
              <div className="relative h-56 overflow-hidden">
                <img src={v.image} alt={v.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                {v.rating && (
                  <span className="absolute top-4 left-4 bg-card text-card-foreground text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1">
                    <Star className="h-3.5 w-3.5 text-accent fill-accent" /> {v.rating}
                  </span>
                )}
              </div>
              <div className="p-6">
                <h3 className="font-heading text-2xl font-normal">{v.title}</h3>
                <p className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1">
                  <MapPin className="h-4 w-4" /> {v.location}
                </p>
                <div className="flex items-center gap-4 mt-4 text-sm text-foreground/70">
                  <span className="flex items-center gap-1.5"><Users className="h-4 w-4 text-accent" /> {v.guests}</span>
                  <span className="flex items-center gap-1.5"><BedDouble className="h-4 w-4 text-accent" /> {v.bedrooms}</span>
                  <span className="flex items-center gap-1.5"><Bath className="h-4 w-4 text-accent" /> {v.baths}</span>
                </div>
                <div className="flex items-baseline justify-between mt-5 pt-4 border-t border-border">
                  <div>
                    <span className="font-heading text-2xl text-accent">{fmtINR(v.base_rate)}</span>
                    <span className="text-xs text-muted-foreground"> / night + taxes</span>
                  </div>
                  <span className="text-sm font-semibold text-primary group-hover:underline">View & Book</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        {filtered.length === 0 && (
          <p className="text-center text-muted-foreground py-16" data-testid="villas-empty">No villas match your search. Try different filters.</p>
        )}
      </div>
    </div>
  );
}
